import type { SparkPoint } from './BinanceEntrySparkline';

/** alert_binance_short_live.py FEE_RT — 차트 청산가 역산에 동일 값 사용. */
export const CHART_FEE_RT = 0.001;

export function kstToMs(kst: string): number {
  return new Date(`${kst.replace(' ', 'T')}+09:00`).getTime();
}

/** settle net(비율) → 청산가. attach_entry_charts() 와 동일 공식. */
/**
 * 롱 쪽 진입 액션. 여기 빠지면 그 레인의 청산점이 진입선 **반대편**에 찍혀
 * "졌는데 이긴 것처럼" 보인다 — 2026-08-14 후보-롱 첫 체결(EDENUSDT 04:09)이
 * 그랬다: 실제로는 0.06073 → 스탑 0.0589 로 떨어져 -3.16% 인데 끝점이
 * 0.06259(진입 +3.1%)로 찍혔다. 이 함수는 백엔드가 붙여준 끝점을 **덮어쓰므로**,
 * 백엔드만 고치면 화면은 그대로 뒤집힌 채 남는다. 새 롱 레인은 여기도 추가할 것.
 * (백엔드 대응물: alert_binance_short_live_monitor.LONG_SETTLE_ACTIONS)
 */
export const LONG_ENTRY_ACTIONS = new Set(['long_entry', 'cl_entry', 'pr_entry', 'gl_entry']);

export function chartExitPx(
  entryPx: number,
  netFrac: number,
  entryAction: string | undefined,
): number {
  const isLong = entryAction != null && LONG_ENTRY_ACTIONS.has(entryAction);
  const chg = isLong ? netFrac + CHART_FEE_RT : -(netFrac + CHART_FEE_RT);
  return entryPx * (1 + chg);
}

export type ChartExitInput = {
  time_kst?: string;
  /** settle_net() 비율 — API net 필드. PnL 표시와 어긋날 수 있어 후순위. */
  net?: number;
  /** PnL 칸과 동일한 % (예: 1.03). 최우선. */
  pct?: number;
  net_usdt?: number;
  notional?: number;
};

/** PnL 칸·원장과 맞는 순수익률(비율). */
export function chartExitNetFrac(exit: ChartExitInput | null | undefined): number | null {
  if (!exit) return null;
  if (exit.pct != null && Number.isFinite(exit.pct)) return exit.pct / 100;
  if (exit.net_usdt != null && exit.notional != null && exit.notional > 0) {
    return exit.net_usdt / exit.notional;
  }
  if (exit.net != null && Number.isFinite(exit.net)) return exit.net;
  return null;
}

/**
 * 1분봉 경로 끝이 실제 청산가와 어긋나는 경우(분봉 최저가가 ● 로 찍힘) PnL 기준
 * 청산 시각·가격으로 끝점을 덮어쓴다.
 */
export function chartPointsWithExit(
  points: SparkPoint[],
  entryPx: number | undefined,
  entryAction: string | undefined,
  exit: ChartExitInput | null | undefined,
): SparkPoint[] {
  if (!exit?.time_kst || entryPx == null || entryPx <= 0 || points.length < 2) {
    return points;
  }
  const netFrac = chartExitNetFrac(exit);
  if (netFrac == null) return points;

  const exitMs = kstToMs(exit.time_kst);
  const exitPx = chartExitPx(entryPx, netFrac, entryAction);
  const trimmed = points.filter((p) => p[0] < exitMs);
  const base = trimmed.length > 0 ? trimmed : [points[0]];
  return [...base, [exitMs, exitPx]];
}
